import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { ScanlineShader } from './shaders/scanline';

const DEEP_SPACE = 0x000008;

export class GameRenderer {
  private renderer!: THREE.WebGLRenderer;
  private composer!: EffectComposer;
  private scanlinePass!: ShaderPass;
  private container!: HTMLElement;
  private clock = new THREE.Clock();
  /**
   * The "preferred" pixel ratio at boot — capped to 1.0 to keep the
   * post-processing chain inside the browser's frame budget. The
   * adaptive quality monitor in `main.ts` may further scale this
   * down via {@link setRenderScale} on sustained slow frames.
   */
  private _basePixelRatio = 1.0;
  private _currentRenderScale = 1.0;
  /**
   * Caller-supplied resize observer, invoked with the post-resize canvas
   * dimensions after the renderer + composer have already been re-sized.
   * The camera aspect lives outside `GameRenderer`, so without this hook
   * the camera's projection matrix would never refresh on orientation
   * change and the scene would render visibly stretched until the next
   * camera-touching code path ran. Leave unset for tests / headless.
   */
  private onResizeCallback: ((w: number, h: number) => void) | null = null;
  /**
   * Pending RAF handle used to coalesce the burst of `resize` /
   * `orientationchange` / `visualViewport.resize` events iOS fires
   * during a device rotation into a single canvas re-layout.
   */
  private rafResizePending: number | null = null;

  init(
    container: HTMLElement,
    onResize?: (w: number, h: number) => void,
  ): void {
    this.container = container;
    this.onResizeCallback = onResize ?? null;

    // `antialias: false` on the WebGLRenderer itself: EffectComposer
    // renders to off-screen render targets that don't share the
    // canvas's default framebuffer multisample resolve, so the only
    // thing canvas-level MSAA bought us was paying for the resolve
    // every frame (plus, on macOS Metal/ANGLE the multisampled
    // default framebuffer is itself a documented source of driver
    // backpressure). The bloom + scanline chain already supplies
    // enough edge-softening for the CRT look; turning MSAA off frees
    // a meaningful chunk of GPU time on every frame. This was the
    // single biggest "the render call ate 60–150 ms while standing
    // still on a planet" contributor in the most recent freeze
    // capture.
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: true,
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
    });

    // Cap pixel ratio at 1.0 (logical pixels). The post-processing
    // pipeline (RenderPass → UnrealBloomPass with 5 mip levels →
    // ScanlineShader) runs the entire frame buffer through ~12 full-
    // screen draws; on a 1920×1080 retina display the previous 1.5×
    // cap meant we were rasterising at 2880×1620 ≈ 4.7 M px/pass,
    // ≈ 56 M px/frame — exactly the workload the user sees as the
    // recurring 50–90 ms `[freeze] FRAME … render=…ms` log lines.
    // CPU profiles show the JS thread at <30 % utilisation during
    // those frames, so the loop is GPU-bound; halving per-pixel work
    // is the cheapest way to take pressure off the bloom/scanline
    // chain. The CRT scanline overlay already stylises away the
    // sub-pixel sharpness, so the visual delta is barely noticeable
    // (and far less noticeable than the freeze itself).
    //
    // {@link setRenderScale} can dial this down further at runtime if
    // the adaptive quality monitor in `main.ts` detects sustained
    // freeze frames — see `_renderQualityScale` there.
    this._basePixelRatio = Math.min(window.devicePixelRatio, 1.0);
    this.renderer.setPixelRatio(this._basePixelRatio);
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setClearColor(DEEP_SPACE, 1);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    container.appendChild(this.renderer.domElement);

    window.addEventListener('resize', this.scheduleResize);
    // iOS Safari / WKWebView fire `orientationchange` BEFORE
    // window.innerWidth/innerHeight are updated to the new orientation.
    // We listen to both events and defer the actual work to a RAF so the
    // measurement always reflects the post-rotate layout. `visualViewport`
    // additionally covers the case where the on-screen keyboard slides
    // down and the page reclaims its full height.
    window.addEventListener('orientationchange', this.scheduleResize);
    if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.scheduleResize);
    }
  }

  setupPostProcessing(scene: THREE.Scene, camera: THREE.Camera): void {
    // Per-tab kill switch. `?nopost=1` (or `localStorage.setItem('nopost','1')`)
    // skips the composer entirely so we can A/B-test whether the recurring
    // surface "render=66ms..." freeze comes from the post-processing chain
    // (UnrealBloom 5-mip downsample + horizontal/vertical blurs + scanline
    // shader → ~18 full-screen draws on top of the scene) or from the
    // scene itself. Bypassing the composer drops the whole post chain
    // and falls back to a direct `renderer.render(scene, camera)`.
    if (this.isPostProcessingDisabled()) {
      console.warn('[renderer] post-processing DISABLED via ?nopost=1 / localStorage');
      this._postDisabled = true;
      this._sceneRef = scene;
      this._cameraRef = camera;
      return;
    }

    const { clientWidth: w, clientHeight: h } = this.container;

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(scene, camera));

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      0.6,   // strength
      0.4,   // radius
      0.85,  // threshold
    );
    this.composer.addPass(bloomPass);
    this._bloomPass = bloomPass;

    this.scanlinePass = new ShaderPass(ScanlineShader);
    this.scanlinePass.uniforms.resolution.value.set(w, h);
    this.composer.addPass(this.scanlinePass);
  }

  private isPostProcessingDisabled(): boolean {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('nopost') === '1') return true;
      if (window.localStorage.getItem('nopost') === '1') return true;
    } catch { /* SSR / sandbox / blocked */ }
    return false;
  }

  /**
   * Per-pass timing for the most recent render. Useful when freeze logs
   * blame the render call but we need to know which pass actually ate the
   * budget. Numbers are wall-clock JS time (composer.render() returns
   * after the WebGL driver acknowledges the submit, so on a backed-up
   * GPU queue this includes driver backpressure).
   */
  private _lastRenderMs = 0;
  private _postDisabled = false;
  private _bloomPass: UnrealBloomPass | null = null;
  private _sceneRef: THREE.Scene | null = null;
  private _cameraRef: THREE.Camera | null = null;

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    const t0 = performance.now();
    if (this._postDisabled) {
      this.renderer.render(scene, camera);
    } else if (this.composer) {
      this.scanlinePass.uniforms.time.value = this.clock.getElapsedTime();
      this.composer.render();
    } else {
      this.renderer.render(scene, camera);
    }
    this._lastRenderMs = performance.now() - t0;
  }

  getLastRenderMs(): number {
    return this._lastRenderMs;
  }

  /**
   * Toggle bloom on/off at runtime without rebuilding the composer.
   * Used by the adaptive quality monitor to shed GPU work before
   * resorting to a render-scale cut (bloom is the single most
   * expensive pass).
   */
  setBloomEnabled(enabled: boolean): void {
    if (this._bloomPass) {
      this._bloomPass.enabled = enabled;
    }
  }

  isBloomEnabled(): boolean {
    return this._bloomPass?.enabled ?? false;
  }

  resize(): void {
    this.applyResize();
  }

  /**
   * Adjust the *effective* pixel ratio at runtime. Used by the
   * adaptive quality monitor: when several consecutive frames blow
   * past the freeze threshold we drop the scale (e.g. 1.0 → 0.75),
   * cutting per-pixel post-processing work by another 44 % and
   * letting the GPU catch back up. Clamped to [0.5, 1.5] so we
   * never fall below something the CRT effect can still sell, and
   * never overshoot the original Retina cap.
   */
  setRenderScale(scale: number): void {
    const clamped = Math.max(0.5, Math.min(1.5, scale));
    if (Math.abs(clamped - this._currentRenderScale) < 0.01) return;
    this._currentRenderScale = clamped;
    const target = this._basePixelRatio * clamped;
    this.renderer.setPixelRatio(target);
    if (this.composer) {
      const w = this.container.clientWidth;
      const h = this.container.clientHeight;
      this.composer.setSize(w, h);
      this.scanlinePass.uniforms.resolution.value.set(w, h);
    }
  }

  getRenderScale(): number {
    return this._currentRenderScale;
  }

  dispose(): void {
    window.removeEventListener('resize', this.scheduleResize);
    window.removeEventListener('orientationchange', this.scheduleResize);
    if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.scheduleResize);
    }
    if (this.rafResizePending != null) {
      cancelAnimationFrame(this.rafResizePending);
      this.rafResizePending = null;
    }
    this.composer?.dispose();
    this.renderer.dispose();
    this.container.removeChild(this.renderer.domElement);
  }

  getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  private scheduleResize = (): void => {
    // Coalesce: iOS fires resize + orientationchange + visualViewport
    // back-to-back during a rotation, and the dimensions on the synchronous
    // path of the first event aren't always the final ones. RAF after the
    // rotation finishes ensures `clientWidth` / `clientHeight` reflect the
    // settled layout.
    if (this.rafResizePending != null) return;
    this.rafResizePending = requestAnimationFrame(() => {
      this.rafResizePending = null;
      this.applyResize();
    });
  };

  private applyResize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (w <= 0 || h <= 0) return;

    this.renderer.setSize(w, h);

    if (this.composer) {
      this.composer.setSize(w, h);
      this.scanlinePass.uniforms.resolution.value.set(w, h);
    }

    this.onResizeCallback?.(w, h);
  }
}
